import type { AstroAdapter, AstroConfig, AstroIntegration } from 'astro'
import { emptyDir, writeJson } from '@astrojs/internal-helpers/fs'
import { getInjectableAnalyticsContent } from './analytics.js'
import {
    DevImageService,
    getDefaultImageConfig,
    getImageConfig,
    WlodevImageConfig,
} from './images/config.js'

const WLODEV_DEFAULT_HOST = 'cdn.wlodev.com'
const PACKAGE_NAME = '@wlodev/astro/adapter'
const OUTPUT_DIR = '.wlodev/output'
const OUTPUT_DIR_PATH = `./${OUTPUT_DIR}`

export const getAdapter = (): AstroAdapter => {
    return {
        name: PACKAGE_NAME,
        entrypointResolution: 'auto',
        supportedAstroFeatures: {
            sharpImageService: 'stable',
            staticOutput: 'stable',
            serverOutput: 'unsupported',
            hybridOutput: 'unsupported',
            envGetSecret: 'unsupported',
        },
        adapterFeatures: {
            edgeMiddleware: false,
            buildOutput: 'static',
        },
    }
}

export type WlodevAdapterConfig = {
    imageService?: {
        dev?: DevImageService
        config?: WlodevImageConfig
    }
}

export default function createIntegration(
    integrationConfig?: WlodevAdapterConfig,
): AstroIntegration {
    let _config: AstroConfig

    return {
        name: PACKAGE_NAME,
        hooks: {
            'astro:config:setup': async ({
                updateConfig,
                config,
                injectScript,
                command,
                logger,
            }) => {
                injectScript(
                    'head-inline',
                    await getInjectableAnalyticsContent({
                        mode: command === 'dev' ? 'development' : 'production',
                    }),
                )

                const imageService = integrationConfig?.imageService
                updateConfig({
                    build: {
                        format: 'directory',
                    },
                    ...getImageConfig(
                        true,
                        imageService ? imageService.config : undefined,
                        config.image,
                        imageService ? imageService.dev ?? 'sharp' : 'sharp',
                        command,
                        logger,
                    ),
                })

                updateConfig({
                    image: {
                        ...(command === 'dev'
                            ? {
                                  remotePatterns: [
                                      ...(config.image?.remotePatterns || []),
                                      {
                                          hostname: '127.0.0.1',
                                      },
                                      {
                                          hostname: 'localhost',
                                      },
                                  ],
                              }
                            : {}),
                    },
                })
            },
            'astro:config:done': ({ setAdapter, config }) => {
                setAdapter(getAdapter())
                _config = config
            },
            'astro:build:start': async () => {
                await emptyDir(new URL(`${OUTPUT_DIR_PATH}/`, _config.root))
            },
            'astro:build:done': async () => {
                const siteId = process.env.WLODEV_CLOUD_SITE_ID
                const imageService = integrationConfig?.imageService ?? {}

                await writeJson(
                    new URL(`${OUTPUT_DIR_PATH}/config.json`, _config.root),
                    {
                        version: 1,
                        routes: [],
                        ...(imageService || _config.image
                            ? {
                                  images: {
                                      ...(imageService.config
                                          ? imageService.config
                                          : getDefaultImageConfig(
                                                _config.image,
                                            )),
                                      domains: [
                                          ...(imageService.config?.domains ??
                                              []),
                                          ..._config.image.domains,
                                      ],
                                      remotePatterns: [
                                          ...(siteId
                                              ? [
                                                    {
                                                        protocol: 'https',
                                                        hostname:
                                                            WLODEV_DEFAULT_HOST,
                                                        pathname: `/${siteId}/**`,
                                                    },
                                                ]
                                              : []),
                                          ...(imageService.config
                                              ?.remotePatterns ?? []),
                                          ..._config.image.remotePatterns,
                                      ],
                                  },
                              }
                            : {}),
                    },
                )
            },
        },
    }
}
